import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

// Import complet des icônes nécessaires
import { 
  Bed, BedDouble, Bath, Users, Clock, Wifi, Tv, Car, Utensils, 
  ChevronLeft, ChevronRight, Heater, Sofa, Gamepad2, Table, Home, 
  Book, DoorOpen, WashingMachine, Baby, Trees, Sun, Shirt, 
  ChefHat, Flame, Droplets, Snowflake 
} from 'lucide-react';

import BedroomDialog, { BedroomData } from '@/components/BedroomDialog';
import { useBedrooms } from '@/hooks/useBedrooms';
import { Room } from '@/data/rooms';
import { useRooms } from '@/hooks/useRooms';
import { Hotspot, hotspots } from '@/data/hotspots';
import { getImageUrl } from '@/lib/image-url';

// Mapping enrichi pour correspondre aux données de rooms.ts
const iconMapping = {
  bed: Bed,
  'bed-double': BedDouble,
  bath: Bath,
  users: Users,
  clock: Clock,
  wifi: Wifi,
  tv: Tv,
  car: Car,
  utensils: Utensils,
  heater: Heater,
  sofa: Sofa,
  gamepad: Gamepad2,
  table: Table,
  home: Home,
  book: Book,
  'door-open': DoorOpen,
  'washing-machine': WashingMachine,
  baby: Baby,
  trees: Trees,
  sun: Sun,
  shirt: Shirt,
  'chef-hat': ChefHat,
  flame: Flame,
  droplets: Droplets,
  snowflake: Snowflake
};

const SLIDE_DURATION = 2500; // ms par hotspot, doit correspondre à animationDuration
const FADE_DURATION = 600; // ms de fondu croisé

const HotspotMap = () => {
  const bedrooms = useBedrooms();
  const rooms = useRooms();
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [isBedroomDialogOpen, setIsBedroomDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedBedroom, setSelectedBedroom] = useState<BedroomData | null>(null);
  const [isRoomFullScreen, setIsRoomFullScreen] = useState(false);
  const [roomFullScreenIndex, setRoomFullScreenIndex] = useState(0);
  const defaultMapImage = getImageUrl("hotspotMap/plan-vue_de_haut-maison-ty-gwennili.webp");
  const [bgImage, setBgImage] = useState(defaultMapImage);
  const [overlayImage, setOverlayImage] = useState<string | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState<string | null>(null);
  const [autoActive, setAutoActive] = useState<string | null>(null);
  const isUserInteracting = useRef(false);
  const autoIndexRef = useRef(0);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const currentImageRef = useRef(defaultMapImage);

  const crossfadeTo = (nextImage: string) => {
    if (currentImageRef.current === nextImage) return;
    currentImageRef.current = nextImage;
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setOverlayImage(nextImage);
    setOverlayVisible(false);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setOverlayVisible(true));
    });
    fadeTimeoutRef.current = setTimeout(() => {
      setBgImage(nextImage);
      setOverlayVisible(false);
      setOverlayImage(null);
    }, FADE_DURATION);
  };

  // Préchargement
  useEffect(() => {
    hotspots.forEach((hotspot) => {
      if (hotspot.hoverImage) {
        const img = new Image();
        img.src = hotspot.hoverImage;
      }
    });
  }, []);

  // Slideshow automatique
  useEffect(() => {
    const hotspotsWithImage = hotspots.filter(h => h.hoverImage);
    let resetTimeout: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (isUserInteracting.current) return;
      const hotspot = hotspotsWithImage[autoIndexRef.current % hotspotsWithImage.length];
      crossfadeTo(hotspot.hoverImage!);
      setAutoActive(hotspot.id);
      autoIndexRef.current++;
      resetTimeout = setTimeout(() => {
        if (!isUserInteracting.current) {
          crossfadeTo(defaultMapImage);
          setAutoActive(null);
        }
      }, SLIDE_DURATION - FADE_DURATION - 100);
    };
    tick();
    const interval = setInterval(tick, SLIDE_DURATION);
    return () => {
      clearInterval(interval);
      clearTimeout(resetTimeout);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleHotspotClick = (roomId: string) => {
    const bedroom = bedrooms.find(b => b.id === roomId);
    if (bedroom) {
      setSelectedBedroom(bedroom);
      setIsBedroomDialogOpen(true);
      return;
    }
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      setSelectedRoom(room);
      setIsRoomDialogOpen(true);
    }
  };

  const handleHotspotHover = (hotspot: Hotspot, isHovering: boolean) => {
    isUserInteracting.current = isHovering;
    if (isHovering && hotspot.hoverImage) {
      crossfadeTo(hotspot.hoverImage);
      setHoveredHotspot(hotspot.id);
      setAutoActive(null);
    } else {
      crossfadeTo(defaultMapImage);
      setHoveredHotspot(null);
    }
  };

  return (
    <div className="bg-white rounded-none sm:rounded-lg py-4 sm:p-6 shadow-none sm:shadow-md w-full">
      <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-center px-4 sm:px-0">Plan intéractif de la maison</h3>
        <p className="text-gray-500 text-sm sm:text-base text-center mb-6 px-4">Cliquez sur les points pour explorer chaque pièce en détail</p>
      <div className="relative w-full sm:max-w-4xl sm:mx-auto">
        <div className="relative w-full aspect-[4/4] overflow-hidden rounded-none sm:rounded-lg">
          <img
            src={bgImage}
            alt="Plan de la maison"
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          {overlayImage && (
            <img
              src={overlayImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain"
              style={{
                opacity: overlayVisible ? 1 : 0,
                transition: `opacity ${FADE_DURATION}ms ease-in-out`,
              }}
              loading="lazy"
              decoding="async"
            />
          )}

          
          {hotspots.map((hotspot) => {
            const isActive = hoveredHotspot === hotspot.id || autoActive === hotspot.id;
            return (
              <button
                key={hotspot.id}
                onClick={() => handleHotspotClick(hotspot.roomId)}
                onMouseEnter={() => handleHotspotHover(hotspot, true)}
                onMouseLeave={() => handleHotspotHover(hotspot, false)}
                className={`absolute w-6 h-6 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-all duration-200 flex items-center justify-center group z-10 ${
                  isActive ? 'bg-[#919682] scale-110' : 'bg-[#C1C8AF] hover:bg-[#919682]'
                }`}
                style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                aria-label={`Voir les détails de ${hotspot.label}`}
              >
                <span
                  className={`absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping ${isActive ? 'bg-[#919682]' : 'bg-[#C1C8AF]'}`}
                  style={{ animationDuration: `${SLIDE_DURATION}ms` }}
                />
                <div className="w-2 h-2 bg-white rounded-full relative z-10"></div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20">
                  {hotspot.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
        {selectedRoom && (
          <DialogContent className="p-0 max-w-4xl w-[90vw] max-h-[92vh] lg:max-h-[90vh] bg-white flex flex-col">
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle className="text-2xl font-bold text-gray-800">{selectedRoom.name}</DialogTitle>
              <DialogDescription className="sr-only">Détails de {selectedRoom.name}</DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-[calc(92vh-140px)] lg:h-[calc(90vh-140px)]">
              {/* Layout Desktop */}
              <div className="hidden lg:block">
                <div className="w-full px-6 mb-6">
                  <div className="w-full aspect-video relative overflow-hidden rounded-lg group bg-gray-100">
                    <Swiper
                      spaceBetween={0}
                      slidesPerView={1}
                      pagination={{ clickable: true }}
                      navigation={{ prevEl: '.swiper-nav-prev-room-desktop', nextEl: '.swiper-nav-next-room-desktop' }}
                      modules={[Pagination, Navigation]}
                      className="w-full h-full"
                    >
                      {selectedRoom.images.map((image, index) => (
                        <SwiperSlide key={index} className="w-full h-full">
                          <img src={image} alt={`Photo de ${selectedRoom.name}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <button className="swiper-nav-prev-room-desktop hidden group-hover:flex absolute top-1/2 -translate-y-1/2 left-4 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow"><ChevronLeft className="h-5 w-5"/></button>
                    <button className="swiper-nav-next-room-desktop hidden group-hover:flex absolute top-1/2 -translate-y-1/2 right-4 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow"><ChevronRight className="h-5 w-5"/></button>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-800">Présentation</h4>
                      <p className="text-gray-700 leading-relaxed">{selectedRoom.description}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-800">Équipements principaux</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {selectedRoom.features.map((feature, index) => {
                          const IconComponent = iconMapping[feature.icon as keyof typeof iconMapping] || Utensils;
                          return (
                            <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100 transition-colors hover:bg-gray-100">
                              <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                                <IconComponent className="h-5 w-5 text-[#919682]" />
                              </div>
                              <span className="text-gray-700 font-medium">{feature.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Layout Mobile */}
              <div className="lg:hidden">
                <div className="space-y-4">
                  <div className="px-4">
                    <div className="w-full h-[45vh] relative rounded-lg overflow-hidden shadow-lg group bg-gray-100">
                      <Swiper
                        spaceBetween={0}
                        slidesPerView={1}
                        pagination={{ clickable: true }}
                        navigation={{ prevEl: '.swiper-nav-prev-room-mobile', nextEl: '.swiper-nav-next-room-mobile' }}
                        modules={[Pagination, Navigation]}
                        className="w-full h-full rounded-lg"
                      >
                        {selectedRoom.images.map((image, index) => (
                          <SwiperSlide key={index} className="w-full h-full">
                            <img
                              src={image}
                              alt={`Photo mobile ${index}`}
                              className="w-full h-full object-cover cursor-pointer"
                              loading="lazy"
                              decoding="async"
                              onClick={() => { setRoomFullScreenIndex(index); setIsRoomFullScreen(true); }}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <button className="swiper-nav-prev-room-mobile flex absolute top-1/2 -translate-y-1/2 left-2 z-10 p-2 bg-white/70 rounded-full shadow"><ChevronLeft className="h-4 w-4"/></button>
                      <button className="swiper-nav-next-room-mobile flex absolute top-1/2 -translate-y-1/2 right-2 z-10 p-2 bg-white/70 rounded-full shadow"><ChevronRight className="h-4 w-4"/></button>
                    </div>
                    <p className="text-center text-xs text-gray-400 mt-2">Appuyez sur une photo pour l'agrandir</p>
                  </div>
                  <div className="px-4 pb-4 space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold mb-2 text-gray-800">Présentation</h4>
                      <p className="text-gray-700 leading-relaxed text-sm">{selectedRoom.description}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-800">Équipements</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {selectedRoom.features.map((feature, index) => {
                          const IconComponent = iconMapping[feature.icon as keyof typeof iconMapping] || Utensils;
                          return (
                            <div key={index} className="flex items-center gap-4 p-2 bg-gray-50 rounded-xl border border-gray-100">
                              <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                                <IconComponent className="h-4 w-4 text-[#919682]" />
                              </div>
                              <span className="text-gray-700 text-sm font-medium">{feature.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </DialogContent>
        )}
      </Dialog>

      {/* Plein écran mobile pour les pièces */}
      <Dialog open={isRoomFullScreen} onOpenChange={setIsRoomFullScreen}>
        <DialogContent className="p-0 max-w-full w-screen h-screen bg-black flex items-center justify-center lg:hidden">
          <DialogDescription className="sr-only">Image en plein écran</DialogDescription>
          {selectedRoom && (
            <div className="relative w-full h-full">
              <Swiper
                spaceBetween={0}
                slidesPerView={1}
                initialSlide={roomFullScreenIndex}
                navigation={{ prevEl: '.swiper-btn-room-fs-prev', nextEl: '.swiper-btn-room-fs-next' }}
                modules={[Navigation]}
                className="w-full h-full"
              >
                {selectedRoom.images.map((image, index) => (
                  <SwiperSlide key={index} className="flex items-center justify-center">
                    <img src={image} alt={`Photo plein écran ${index + 1}`} className="max-w-full max-h-full object-contain" loading="lazy" decoding="async" />
                  </SwiperSlide>
                ))}
              </Swiper>
              <button className="swiper-btn-room-fs-prev absolute top-1/2 left-4 z-10 p-3 bg-white/70 rounded-full"><ChevronLeft className="h-6 w-6"/></button>
              <button className="swiper-btn-room-fs-next absolute top-1/2 right-4 z-10 p-3 bg-white/70 rounded-full"><ChevronRight className="h-6 w-6"/></button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BedroomDialog 
        isOpen={isBedroomDialogOpen} 
        onOpenChange={setIsBedroomDialogOpen} 
        bedroom={selectedBedroom} 
      />
    </div>
  );
};

export default HotspotMap;